import { useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase";
import { home } from "../content/home";
import { cn } from "../lib/cn";

const { label, headline, body, tools, request } = home.integrations;

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper";
// Tiles sit like stickers on the page, each at its own small angle.
const TILT = ["-rotate-2", "rotate-1", "rotate-2", "-rotate-1"];

type RequestStatus = "idle" | "loading" | "success" | "error";

function IntegrationRequest() {
  const [tool, setTool] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<RequestStatus>("idle");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!tool.trim()) return;
    setStatus("loading");
    try {
      const { error } = await supabase
        .from("integration_requests")
        .insert({ tool: tool.trim(), email: email.trim() || null });
      setStatus(error ? "error" : "success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return <p className="mt-8 font-hand text-2xl text-char">Thanks. We'll let you know when it ships.</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 max-w-md">
      <div className="flex flex-col gap-3">
        <input
          type="text"
          value={tool}
          onChange={(e) => setTool(e.target.value)}
          placeholder={request.placeholder}
          aria-label={request.placeholder}
          required
          className="h-11 rounded-[10px] border border-char bg-paper-raised px-4 font-sans text-sm text-char placeholder:text-char/50 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email, so we can tell you (optional)"
            aria-label="Email (optional)"
            className="h-11 flex-1 rounded-[10px] border border-char bg-paper-raised px-4 font-sans text-sm text-char placeholder:text-char/50 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className={cn(
              "h-11 shrink-0 rounded-[10px] border border-char bg-char px-5 font-sans text-sm font-semibold text-paper transition-opacity disabled:opacity-60",
              focusRing,
            )}
          >
            {status === "loading" ? "Sending" : request.button}
          </button>
        </div>
      </div>
      {status === "error" && (
        <p role="alert" className="mt-2 font-sans text-sm text-[#b42318]">
          Couldn't send your request. Check your connection and try again.
        </p>
      )}
    </form>
  );
}

export function IntegrationsSection() {
  return (
    <section id="integrations" aria-label={label} className="scroll-mt-20 px-4 py-24 sm:px-6 lg:py-28">
      <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-2">
        <div>
          <h2 className="font-serif text-[clamp(2.25rem,4.5vw,3.5rem)] font-medium leading-[1.02] tracking-[-0.015em] text-char">
            {headline.line1}
            <br />
            <em className="text-brand-500">{headline.line2}</em>
          </h2>
          <p className="mt-5 max-w-md font-sans text-lg leading-relaxed text-char/75">{body}</p>
          <IntegrationRequest />
        </div>

        <ul aria-label="Integrations" className="mx-auto grid w-full max-w-sm grid-cols-2 gap-4 sm:gap-6">
          {tools.map((tool, i) => (
            <li
              key={tool.name}
              className={cn(
                "flex aspect-[5/4] flex-col items-center justify-center gap-3 rounded-2xl border border-char bg-paper-raised shadow-offset-sm",
                TILT[i],
              )}
            >
              <img src={tool.logo} alt="" loading="lazy" className="h-9 w-9 object-contain" />
              <span className="font-serif text-xl text-char">{tool.name}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
